/**
 * Household booking ledger.
 * Answers: allocated share, supplier balance, reimbursement claims.
 * Inputs: bookings + payments + refunds + settlements. Outputs: derived balances.
 */

import type {
  Allocation,
  Booking,
  HouseholdSettlement,
  SupplierPayment,
  SupplierRefund,
  TripDocument,
} from "./schema.ts";
import { allocationsSumToTotal, money, type Money } from "./money.ts";

export type Claim = {
  fromHouseholdId: string;
  toHouseholdId: string;
  currency: string;
  amountMinor: number;
};

export type BookingLedger = {
  bookingId: string;
  currency: string | null;
  total: Money | null;
  allocated: Record<string, number>;
  supplierRemaining: number;
  funded: Record<string, number>;
  claims: Claim[];
  missingCost: boolean;
  unallocated: boolean;
  cancelled: boolean;
  fundingReversalIncomplete: boolean;
  hasFinancialHistory: boolean;
  errors: string[];
};

export function householdSupplierRemaining(ledger: BookingLedger, householdId: string): number {
  if (!ledger.currency || ledger.missingCost) return 0;
  if (ledger.cancelled) {
    if (ledger.supplierRemaining <= 0) return 0;
    const totalAlloc = Object.values(ledger.allocated).reduce((sum, amount) => sum + amount, 0);
    if (totalAlloc <= 0) return 0;
    return Math.max(0, Math.trunc((ledger.supplierRemaining * (ledger.allocated[householdId] ?? 0)) / totalAlloc));
  }
  return Math.max(0, (ledger.allocated[householdId] ?? 0) - (ledger.funded[householdId] ?? 0));
}

function emptyAllocMap(householdIds: string[]): Record<string, number> {
  return Object.fromEntries(householdIds.map((id) => [id, 0]));
}

function addClaim(claimMap: Map<string, Claim>, claim: Claim) {
  if (claim.amountMinor === 0) return;
  const key = `${claim.fromHouseholdId}->${claim.toHouseholdId}:${claim.currency}`;
  const existing = claimMap.get(key);
  if (existing) existing.amountMinor += claim.amountMinor;
  else claimMap.set(key, { ...claim });
}

function reverseAllocationsForRefund(
  refund: SupplierRefund,
  payments: SupplierPayment[],
): { rows: Allocation[]; payerHouseholdId: string | null; incomplete: boolean } {
  const payment = refund.paymentId ? payments.find((row) => row.id === refund.paymentId) : undefined;
  const payerHouseholdId = refund.recipientHouseholdId ?? payment?.payerHouseholdId ?? null;
  if (refund.reversedAllocations.length > 0) {
    return { rows: refund.reversedAllocations, payerHouseholdId, incomplete: false };
  }
  if (payment) {
    if (refund.credit.amountMinor === payment.credit.amountMinor) {
      return { rows: payment.fundedAllocations, payerHouseholdId, incomplete: false };
    }
    if (payment.credit.amountMinor > 0) {
      let used = 0;
      const rows = payment.fundedAllocations.map((row, index) => {
        const share = index === payment.fundedAllocations.length - 1
          ? refund.credit.amountMinor - used
          : Math.trunc((refund.credit.amountMinor * row.amountMinor) / payment.credit.amountMinor);
        used += share;
        return { householdId: row.householdId, amountMinor: share };
      });
      return { rows, payerHouseholdId, incomplete: false };
    }
  }
  return {
    rows: [],
    payerHouseholdId,
    incomplete: refund.credit.amountMinor > 0,
  };
}

export function bookingLedger(
  booking: Booking,
  payments: SupplierPayment[],
  refunds: SupplierRefund[],
  householdIds: string[],
): BookingLedger {
  const cancelled = booking.bookingStatus === "cancelled";
  if (!booking.cost) {
    const relatedPayments = payments.filter((p) => p.bookingId === booking.id);
    const relatedRefunds = refunds.filter((r) => r.bookingId === booking.id);
    return {
      bookingId: booking.id,
      currency: null,
      total: null,
      allocated: emptyAllocMap(householdIds),
      supplierRemaining: 0,
      funded: emptyAllocMap(householdIds),
      claims: [],
      missingCost: true,
      unallocated: false,
      cancelled,
      fundingReversalIncomplete: false,
      hasFinancialHistory: relatedPayments.length > 0 || relatedRefunds.length > 0,
      errors: [],
    };
  }

  const unallocated = !allocationsSumToTotal(booking.cost, booking.allocations);

  const allocated = emptyAllocMap(householdIds);
  for (const row of booking.allocations) {
    allocated[row.householdId] = (allocated[row.householdId] ?? 0) + row.amountMinor;
  }

  const relatedPayments = payments.filter((p) => p.bookingId === booking.id);
  const relatedRefunds = refunds.filter((r) => r.bookingId === booking.id);
  let credited = 0;
  const funded = emptyAllocMap(householdIds);
  const claimMap = new Map<string, Claim>();
  let fundingReversalIncomplete = false;
  const errors: string[] = [];

  for (const payment of relatedPayments) {
    if (payment.credit.currency !== booking.cost.currency) {
      errors.push(`Payment currency mismatch on ${booking.id}`);
      continue;
    }
    const fundedSum = payment.fundedAllocations.reduce((sum, row) => sum + row.amountMinor, 0);
    if (fundedSum !== payment.credit.amountMinor) {
      errors.push(`Funded allocations must equal credited amount for ${payment.id}`);
      continue;
    }
    credited += payment.credit.amountMinor;
    for (const row of payment.fundedAllocations) {
      funded[row.householdId] = (funded[row.householdId] ?? 0) + row.amountMinor;
      if (row.householdId !== payment.payerHouseholdId && row.amountMinor > 0) {
        addClaim(claimMap, {
          fromHouseholdId: row.householdId,
          toHouseholdId: payment.payerHouseholdId,
          currency: payment.credit.currency,
          amountMinor: row.amountMinor,
        });
      }
    }
  }

  for (const refund of relatedRefunds) {
    credited -= refund.credit.amountMinor;
    const reversed = reverseAllocationsForRefund(refund, relatedPayments);
    if (reversed.incomplete) fundingReversalIncomplete = true;
    for (const row of reversed.rows) {
      funded[row.householdId] = (funded[row.householdId] ?? 0) - row.amountMinor;
      if (reversed.payerHouseholdId && row.householdId !== reversed.payerHouseholdId && row.amountMinor > 0) {
        addClaim(claimMap, {
          fromHouseholdId: row.householdId,
          toHouseholdId: reversed.payerHouseholdId,
          currency: refund.credit.currency,
          amountMinor: -row.amountMinor,
        });
      }
    }
  }

  const cancellationCharge = [...relatedRefunds].reverse().find((row) => row.cancellationCharge)?.cancellationCharge;
  let supplierRemaining = booking.cost.amountMinor - credited;
  if (cancelled) {
    if (cancellationCharge && cancellationCharge.currency === booking.cost.currency) {
      supplierRemaining = cancellationCharge.amountMinor;
    } else if (relatedPayments.length === 0 && relatedRefunds.length === 0) {
      supplierRemaining = 0;
    } else if (relatedRefunds.length > 0 && credited <= 0) {
      supplierRemaining = 0;
    }
  }

  const claims = [...claimMap.values()].filter((c) => c.amountMinor !== 0);
  const hasFinancialHistory =
    relatedPayments.length > 0
    || relatedRefunds.length > 0
    || supplierRemaining !== 0
    || claims.length > 0
    || Object.values(funded).some((n) => n !== 0);

  return {
    bookingId: booking.id,
    currency: booking.cost.currency,
    total: money(booking.cost.currency, booking.cost.amountMinor),
    allocated,
    supplierRemaining,
    funded,
    claims,
    missingCost: false,
    unallocated,
    cancelled,
    fundingReversalIncomplete,
    hasFinancialHistory,
    errors,
  };
}

export type UnallocatedCredit = {
  fromHouseholdId: string;
  toHouseholdId: string;
  currency: string;
  amountMinor: number;
};

export function applySettlements(claims: Claim[], settlements: HouseholdSettlement[]): {
  claims: Claim[];
  unallocatedCredits: UnallocatedCredit[];
} {
  const next = claims.map((c) => ({ ...c }));
  const unallocatedCredits: UnallocatedCredit[] = [];

  for (const settlement of settlements) {
    if (settlement.unallocated) {
      unallocatedCredits.push({
        fromHouseholdId: settlement.fromHouseholdId,
        toHouseholdId: settlement.toHouseholdId,
        currency: settlement.amount.currency,
        amountMinor: settlement.amount.amountMinor,
      });
      continue;
    }
    let remaining = settlement.amount.amountMinor;
    for (const claim of next) {
      if (remaining <= 0) break;
      if (
        claim.fromHouseholdId === settlement.fromHouseholdId &&
        claim.toHouseholdId === settlement.toHouseholdId &&
        claim.currency === settlement.amount.currency
      ) {
        const applied = Math.min(claim.amountMinor, remaining);
        claim.amountMinor -= applied;
        remaining -= applied;
      }
    }
    if (remaining > 0) {
      unallocatedCredits.push({
        fromHouseholdId: settlement.fromHouseholdId,
        toHouseholdId: settlement.toHouseholdId,
        currency: settlement.amount.currency,
        amountMinor: remaining,
      });
    }
  }

  return { claims: next.filter((c) => c.amountMinor !== 0), unallocatedCredits };
}

export function tripLedger(doc: TripDocument): {
  bookings: BookingLedger[];
  claims: Claim[];
  unallocatedCredits: UnallocatedCredit[];
  supplierByCurrency: Record<string, number>;
  allocatedByHousehold: Record<string, Record<string, number>>;
  remainingByHousehold: Record<string, Record<string, number>>;
  missingCosts: string[];
} {
  const householdIds = doc.households.map((h) => h.id);
  const allLedgers = doc.bookings.map((booking) =>
    bookingLedger(booking, doc.supplierPayments, doc.supplierRefunds, householdIds),
  );
  const bookings = allLedgers.filter((row) => !row.cancelled || row.hasFinancialHistory);

  const rawClaims = bookings.flatMap((b) => b.claims);
  const settled = applySettlements(rawClaims, doc.householdSettlements);
  const claims = settled.claims;
  const unallocatedCredits = settled.unallocatedCredits;

  const supplierByCurrency: Record<string, number> = {};
  const allocatedByHousehold: Record<string, Record<string, number>> = {};
  const remainingByHousehold: Record<string, Record<string, number>> = {};
  for (const id of householdIds) {
    allocatedByHousehold[id] = {};
    remainingByHousehold[id] = {};
  }

  for (const row of bookings) {
    if (!row.currency || row.missingCost) continue;
    supplierByCurrency[row.currency] = (supplierByCurrency[row.currency] ?? 0) + row.supplierRemaining;
    for (const [hid, amount] of Object.entries(row.allocated)) {
      if (!row.cancelled) {
        allocatedByHousehold[hid][row.currency] = (allocatedByHousehold[hid][row.currency] ?? 0) + amount;
      }
      const leftover = householdSupplierRemaining(row, hid);
      remainingByHousehold[hid][row.currency] = (remainingByHousehold[hid][row.currency] ?? 0) + leftover;
    }
  }

  return {
    bookings,
    claims,
    unallocatedCredits,
    supplierByCurrency,
    allocatedByHousehold,
    remainingByHousehold,
    missingCosts: bookings.filter((b) => b.missingCost && !b.cancelled).map((b) => b.bookingId),
  };
}

export function knownCostTotals(doc: TripDocument): {
  byCurrency: Record<string, number>;
  hotelByCurrency: Record<string, number>;
  complete: false;
} {
  const byCurrency: Record<string, number> = {};
  const hotelByCurrency: Record<string, number> = {};
  for (const booking of doc.bookings) {
    if (booking.bookingStatus === "cancelled" || !booking.cost) continue;
    byCurrency[booking.cost.currency] = (byCurrency[booking.cost.currency] ?? 0) + booking.cost.amountMinor;
    if (booking.kind === "hotel_reservation") {
      hotelByCurrency[booking.cost.currency] =
        (hotelByCurrency[booking.cost.currency] ?? 0) + booking.cost.amountMinor;
    }
  }
  return { byCurrency, hotelByCurrency, complete: false };
}

export function previewPayment(
  booking: Booking,
  payments: SupplierPayment[],
  refunds: SupplierRefund[],
  householdIds: string[],
  input: Pick<SupplierPayment, "payerHouseholdId" | "credit" | "fundedAllocations">,
): { supplierRemaining: number; claims: Claim[] } {
  const hypothetical: SupplierPayment = {
    id: "preview",
    bookingId: booking.id,
    payerHouseholdId: input.payerHouseholdId,
    credit: input.credit,
    fundedAllocations: input.fundedAllocations,
    date: "preview",
    idempotencyKey: "preview",
    createdAt: "preview",
    createdBy: "preview",
  };
  const ledger = bookingLedger(booking, [...payments, hypothetical], refunds, householdIds);
  return { supplierRemaining: ledger.supplierRemaining, claims: ledger.claims };
}

export function currentPaymentState(ledger: BookingLedger): "unpaid" | "partial" | "paid" | "refunded" | "cancelled" {
  if (ledger.cancelled && ledger.supplierRemaining === 0 && ledger.claims.length === 0) {
    const funded = Object.values(ledger.funded).reduce((sum, n) => sum + n, 0);
    return funded < 0 || ledger.hasFinancialHistory ? "cancelled" : "cancelled";
  }
  if (ledger.cancelled) return "cancelled";
  if (ledger.missingCost) return "unpaid";
  const funded = Object.values(ledger.funded).reduce((sum, n) => sum + n, 0);
  if (ledger.supplierRemaining <= 0 && funded > 0) return "paid";
  if (funded > 0 || ledger.supplierRemaining < (ledger.total?.amountMinor ?? 0)) return "partial";
  return "unpaid";
}
